/**
 * Input Validation Utilities
 *
 * Provides functions to validate user input on the frontend
 * to prevent injection attacks and provide immediate feedback.
 */

// Maximum lengths for text fields
const MAX_NOTES_LENGTH = 1000;
const MAX_HEDGE_OBJECT_LENGTH = 50;

// Dangerous patterns to detect and block
const DANGEROUS_PATTERNS = [
  // XSS - Script tags and JavaScript
  { pattern: /<script[^>]*>.*?<\/script>/i, message: 'Script tags are not allowed' },
  { pattern: /javascript:/i, message: 'JavaScript URLs are not allowed' },
  { pattern: /onerror\s*=/i, message: 'Event handlers are not allowed' },
  { pattern: /onclick\s*=/i, message: 'Event handlers are not allowed' },
  { pattern: /onload\s*=/i, message: 'Event handlers are not allowed' },
  { pattern: /eval\s*\(/i, message: 'JavaScript eval is not allowed' },

  // HTML injection
  { pattern: /<iframe[^>]*>/i, message: 'iframes are not allowed' },
  { pattern: /<embed[^>]*>/i, message: 'Embed tags are not allowed' },
  { pattern: /<object[^>]*>/i, message: 'Object tags are not allowed' },
  { pattern: /<img[^>]*>/i, message: 'Image tags are not allowed' },
  { pattern: /<svg[^>]*>/i, message: 'SVG tags are not allowed' },
  { pattern: /<link[^>]*>/i, message: 'Link tags are not allowed' },

  // SQL Injection
  { pattern: /'\s*OR\s*'/i, message: 'SQL operators are not allowed' },
  { pattern: /"\s*OR\s*"/i, message: 'SQL operators are not allowed' },
  { pattern: /'\s*OR\s*1\s*=\s*1/i, message: 'SQL injection patterns are not allowed' },
  { pattern: /--\s*$/i, message: 'SQL comment syntax is not allowed' },
  { pattern: /;\s*DROP\s+TABLE/i, message: 'SQL commands are not allowed' },
  { pattern: /UNION\s+SELECT/i, message: 'SQL commands are not allowed' },
  { pattern: /xp_\w+/i, message: 'SQL stored procedures are not allowed' },
  { pattern: /\bexec\s*\(/i, message: 'SQL exec commands are not allowed' },

  // NoSQL Injection
  { pattern: /\$gt\s*:/i, message: 'Database operators are not allowed' },
  { pattern: /\$lt\s*:/i, message: 'Database operators are not allowed' },
  { pattern: /\$ne\s*:/i, message: 'Database operators are not allowed' },
  { pattern: /\$where\s*:/i, message: 'Database operators are not allowed' },
  { pattern: /\$regex\s*:/i, message: 'Database operators are not allowed' },
  { pattern: /\$eval/i, message: 'Database eval operators are not allowed' },
  { pattern: /\$function/i, message: 'Database function operators are not allowed' },

  // Command Injection
  { pattern: /;\s*rm\s+/i, message: 'Shell commands are not allowed' },
  { pattern: /;\s*cat\s+/i, message: 'Shell commands are not allowed' },
  { pattern: /\$\(/i, message: 'Command substitution is not allowed' },
  { pattern: /`[^`]*`/i, message: 'Command substitution is not allowed' },

  // Path Traversal
  { pattern: /\.\.\/.+\//i, message: 'Path traversal is not allowed' },
  { pattern: /\.\.\\.+\\/i, message: 'Path traversal is not allowed' },
];

/**
 * Generic text field validation for dangerous content
 *
 * @param value - Raw text from user input
 * @param fieldName - Name of the field (for error messages)
 * @param maxLength - Maximum allowed length
 * @returns Object with isValid flag and optional error message
 */
export function validateTextField(
  value: string | null | undefined,
  fieldName: string,
  maxLength: number
): {
  isValid: boolean;
  error?: string;
} {
  // Handle empty/null
  if (!value) {
    return { isValid: true };
  }

  // Convert to string and trim
  const trimmedValue = String(value).trim();

  // Check for dangerous patterns
  for (const { pattern, message } of DANGEROUS_PATTERNS) {
    if (pattern.test(trimmedValue)) {
      return {
        isValid: false,
        error: `${message}. Please remove potentially dangerous content.`,
      };
    }
  }

  // Check length
  if (trimmedValue.length > maxLength) {
    return {
      isValid: false,
      error: `${fieldName} is too long. Maximum ${maxLength} characters allowed.`,
    };
  }

  return { isValid: true };
}

/**
 * Validate Notes field for dangerous content
 *
 * @param notes - Raw notes text from user input
 * @returns Object with isValid flag and optional error message
 */
export function validateNotes(notes: string | null | undefined): {
  isValid: boolean;
  error?: string;
} {
  return validateTextField(notes, 'Notes', MAX_NOTES_LENGTH);
}

/**
 * Validate Hedge Object field for dangerous content
 *
 * @param hedgeObject - Raw hedge object text from user input
 * @returns Object with isValid flag and optional error message
 */
export function validateHedgeObject(hedgeObject: string | null | undefined): {
  isValid: boolean;
  error?: string;
} {
  return validateTextField(hedgeObject, 'Hedge Object', MAX_HEDGE_OBJECT_LENGTH);
}

/**
 * Sanitize Notes field by stripping HTML tags
 * This is a basic sanitization for display purposes.
 * Server-side validation is the primary security layer.
 *
 * @param notes - Raw notes text
 * @returns Sanitized notes text
 */
export function sanitizeNotesForDisplay(notes: string | null | undefined): string {
  if (!notes) {
    return '';
  }

  // Strip HTML tags
  return String(notes).replace(/<[^>]+>/g, '');
}
